/**
 * `selectionSetTransform.ts` — the pivot-delta bulk transform, as pure math.
 *
 * ## What this ports, and why it looks nothing like the Unity code
 *
 * Unity moves a selection set by moving ONE object: `MultiExposeToEditor` is a
 * wrapper GameObject that PARENTS the set's members, so writing its transform
 * moves them for free. The persisted form is a delta rather than that pose —
 * `MultiExposeToEditor.GetTransformChange` (`:123-148`) reads the wrapper's
 * pose in scene space and returns a `BulkEntitySnapshotData` carrying
 *
 *   pivot          = the wrapper origin's position now
 *   positionChange = pivotNow − pivotBefore
 *   rotationChange = rotationNow * inverse(rotationBefore)
 *   scaleChange    = uniform localScale ratio
 *
 * which `SelectedGameObjectModel.UpdateEntityTransform` (`:2139-2146`) hands to
 * `UpdateEntitiesAsync(members, …, UpdateEntityFlags.Transform)`.
 *
 * The web has no wrapper: `SceneEntityRenderer` owns the scene graph and every
 * member is rendered from its own snapshot. Re-parenting the members under a
 * set node would have to be understood by picking, isolation, merge and the
 * per-snapshot persist path — a much larger change than the feature. So the
 * DELTA is the port: the same three numbers, applied here to each member's
 * stored transform, and each member persisted through the ordinary
 * per-snapshot write. The visible result is identical because the members are
 * what is rendered in both clients.
 *
 * ## The pivot is the members' COMMON CENTRE, not their bounds centre
 *
 * `MultiExposeToEditor.RecomputeOrigin` (`:427`) uses
 * `TransformUtility.GetCommonCenter(SelectedSingleObjects transforms)` — the
 * mean of the member TRANSFORM POSITIONS. `ComputeBounds()` exists on the same
 * class and is deliberately not what the origin uses, so do not "improve" this
 * into a bounding-box centre: a set of one large and one small model would then
 * rotate about a different point in each client.
 *
 * Everything here is in SCENE space (Unity's `GetCurrentPoseInSpace`
 * inverse-transforms into `SceneSpace` before reporting), which is the space
 * `EntitySnapshot.transform` is already stored in, so no basis conversion
 * happens on this path.
 */

import type { EntityTransform, Vec3 } from "./entityData";
import {
  quatMultiply,
  rotateVecByQuat,
  unityEulerToQuat,
  unityQuatToEuler,
  type UnityQuat,
} from "@models/utils/unityBasis";

/** The set's own pose, mirroring `SelectionSetData.Position/Rotation/Scale`. */
export interface SelectionSetPose {
  readonly position: Vec3;
  readonly rotation: Vec3;
  readonly scale: Vec3;
}

/** Unity's `BulkEntitySnapshotData` transform payload, minus the wire shape. */
export interface PivotDelta {
  /** Scene-space point the rotation and scale act about. */
  readonly pivot: Vec3;
  readonly positionChange: Vec3;
  readonly rotationChange: UnityQuat;
  /** Uniform, like Unity's — it derives its ratio from `localScale.x` alone. */
  readonly scaleChange: number;
}

export const IDENTITY_SET_POSE: SelectionSetPose = {
  position: [0, 0, 0],
  rotation: [0, 0, 0],
  scale: [1, 1, 1],
};

/** Unity `TransformUtility.GetCommonCenter` — the mean member position. */
export function selectionSetPivot(memberPositions: ReadonlyArray<Vec3>): Vec3 {
  if (memberPositions.length === 0) return [0, 0, 0];
  let x = 0;
  let y = 0;
  let z = 0;
  for (const p of memberPositions) {
    x += p[0];
    y += p[1];
    z += p[2];
  }
  const n = memberPositions.length;
  return [x / n, y / n, z / n];
}

/** Conjugate of a unit quaternion — Unity's `Quaternion.Inverse`. */
export function quatInverse(q: UnityQuat): UnityQuat {
  return [-q[0], -q[1], -q[2], q[3]];
}

/**
 * The change between two set poses, in the shape Unity persists.
 *
 * `scaleChange` reads the X component ONLY, matching
 * `GetTransformChange`'s `transform.localScale.x / m_lastScale.x` — the set
 * scale is uniform upstream, and taking a per-axis ratio here would let a
 * hand-edited row shear the members in a way no Unity client can produce.
 */
export function selectionSetPoseDelta(
  from: SelectionSetPose,
  to: SelectionSetPose,
  pivot: Vec3,
): PivotDelta {
  const fromScale = from.scale[0] === 0 ? 1 : from.scale[0];
  return {
    pivot,
    positionChange: [
      to.position[0] - from.position[0],
      to.position[1] - from.position[1],
      to.position[2] - from.position[2],
    ],
    rotationChange: quatMultiply(
      unityEulerToQuat(to.rotation),
      quatInverse(unityEulerToQuat(from.rotation)),
    ),
    scaleChange: to.scale[0] / fromScale,
  };
}

/** True when the delta would move nothing — used to skip a no-op write. */
export function isIdentityPivotDelta(delta: PivotDelta, epsilon = 1e-6): boolean {
  const movedPosition = delta.positionChange.some((v) => Math.abs(v) > epsilon);
  const movedScale = Math.abs(delta.scaleChange - 1) > epsilon;
  // A unit quaternion is the identity rotation when |w| is 1 — sign-agnostic,
  // because q and −q are the same rotation.
  const movedRotation = Math.abs(Math.abs(delta.rotationChange[3]) - 1) > epsilon;
  return !movedPosition && !movedRotation && !movedScale;
}

/**
 * Apply the delta to one member's stored transform — a rigid move of the set
 * about `pivot`, so the members keep their arrangement relative to each other.
 *
 * The pivot is the one from BEFORE the move (Unity's `m_lastPosition` side of
 * the subtraction), because the rotation has to act on the arrangement as it
 * stood; `positionChange` then carries the whole translation.
 */
export function applyPivotDelta(
  transform: EntityTransform,
  delta: PivotDelta,
): { position: Vec3; rotation: Vec3; scale: Vec3 } {
  const offset: Vec3 = [
    transform.position[0] - delta.pivot[0],
    transform.position[1] - delta.pivot[1],
    transform.position[2] - delta.pivot[2],
  ];
  const rotated = rotateVecByQuat(offset, delta.rotationChange);
  const scaled: Vec3 = [
    rotated[0] * delta.scaleChange,
    rotated[1] * delta.scaleChange,
    rotated[2] * delta.scaleChange,
  ];
  const position: Vec3 = [
    delta.pivot[0] + scaled[0] + delta.positionChange[0],
    delta.pivot[1] + scaled[1] + delta.positionChange[1],
    delta.pivot[2] + scaled[2] + delta.positionChange[2],
  ];
  // `rotationChange * memberRotation` — the delta pre-multiplies, matching
  // `rotationNow * inverse(rotationBefore)` being built the same way round.
  const rotation = unityQuatToEuler(
    quatMultiply(delta.rotationChange, unityEulerToQuat(transform.rotation)),
    "signed",
  );
  const scale: Vec3 = [
    transform.scale[0] * delta.scaleChange,
    transform.scale[1] * delta.scaleChange,
    transform.scale[2] * delta.scaleChange,
  ];
  return { position, rotation, scale };
}
